/**
 * Role access matrix: pure functions over the rows `bp_get_access` returns
 * ({module_id, module_name, create_access, read_access, update_access, delete_access}).
 *
 * `tree` is components/third-party/modules.json: { parent module: [child modules] }. The rules are the ones the
 * Access Control page applied, kept in one place:
 *   - granting Create, Edit or Delete also grants View; removing View removes the rest;
 *   - a parent applies to everything under it, at any depth, and shows the state of those modules (all / some / none);
 *   - a module may sit under several parents, and a parent may itself be a child (BMR Details under Execution Manager).
 */
export const ACCESS_TYPES = [
  { key: 'read_access', label: 'View' },
  { key: 'create_access', label: 'Create' },
  { key: 'update_access', label: 'Edit' },
  { key: 'delete_access', label: 'Delete' },
];
const KEYS = ACCESS_TYPES.map((a) => a.key);
const OTHER = 'Other';

const withFlag = (row, key, checked) => {
  const next = { ...row, [key]: checked };
  if (key !== 'read_access' && checked) next.read_access = true;
  if (key === 'read_access' && !checked) {
    next.create_access = false;
    next.update_access = false;
    next.delete_access = false;
  }
  return next;
};

const sameFlags = (a, b) => KEYS.every((k) => !!a[k] === !!b[k]);

/** The children of a parent (a parent listing only itself has none). */
const childrenOf = (tree, parent) => (tree[parent] || []).filter((name) => name !== parent);

/** Every module under a parent at any depth (Execution Manager -> BMR Details -> Released BMR), once each. */
const descendantsOf = (tree, parent) => {
  const found = [];
  const seen = new Set([parent]);
  const visit = (name) => childrenOf(tree, name).forEach((child) => {
    if (seen.has(child)) return;
    seen.add(child);
    found.push(child);
    visit(child);
  });
  visit(parent);
  return found;
};

/** Set one flag on one module; returns new rows. Cascades down to its children and up to the parents holding it. */
export function setAccess(rows, moduleName, key, checked, tree) {
  let next = rows.map((r) => (r.module_name === moduleName ? withFlag(r, key, checked) : r));
  const changed = [moduleName];
  const kids = descendantsOf(tree, moduleName);
  if (kids.length) {
    next = next.map((r) => (kids.includes(r.module_name) ? withFlag(r, key, checked) : r));
    changed.push(...kids);
  }
  // A parent has a flag when any of its children has it. Walk up from every module that changed.
  const queue = [...changed];
  const seen = new Set();
  while (queue.length) {
    const name = queue.shift();
    Object.keys(tree).forEach((parent) => {
      if (parent === name || !childrenOf(tree, parent).includes(name)) return;
      const kidRows = next.filter((r) => childrenOf(tree, parent).includes(r.module_name));
      const parentRow = next.find((r) => r.module_name === parent);
      if (!parentRow || !kidRows.length) return;
      const derived = { ...parentRow };
      KEYS.forEach((k) => { derived[k] = kidRows.some((r) => r[k]); });
      if (!sameFlags(parentRow, derived)) {
        next = next.map((r) => (r.module_name === parent ? derived : r));
        if (!seen.has(parent)) { seen.add(parent); queue.push(parent); }
      }
    });
  }
  return next;
}

/** All / some / none of the children (or the module itself when it has none) for one flag. */
export function flagState(rows, moduleName, key, tree) {
  const kids = descendantsOf(tree, moduleName);
  const subjects = kids.length ? rows.filter((r) => kids.includes(r.module_name)) : rows.filter((r) => r.module_name === moduleName);
  const on = subjects.filter((r) => r[key]).length;
  return { checked: subjects.length > 0 && on === subjects.length, indeterminate: on > 0 && on < subjects.length };
}

/** The groups to show: one per parent in the tree (in its order), then the modules in no group under "Other". */
export function groupModules(rows, tree) {
  const byName = new Map(rows.map((r) => [r.module_name, r]));
  const groups = [];
  const placed = new Set();
  Object.keys(tree).forEach((parent) => {
    if (!byName.has(parent)) return;
    const kids = childrenOf(tree, parent).filter((n) => byName.has(n));
    groups.push({ name: parent, parent: byName.get(parent), children: kids.map((n) => byName.get(n)) });
    placed.add(parent);
    kids.forEach((n) => placed.add(n));
  });
  const rest = rows.filter((r) => !placed.has(r.module_name));
  if (rest.length) groups.push({ name: OTHER, parent: null, children: rest });
  return groups;
}

/** A starting point for a new role: View on every module, nothing else (what a new role has always been given). */
export const viewOnly = (rows) => rows.map((r) => ({ ...r, read_access: true, create_access: false, update_access: false, delete_access: false }));
